/**
 * Typed client for /signing/* — backs the Signing Approvals screen.
 *
 * This is the signing-request APPROVAL workflow (manual-review queue,
 * automated-tier oversight, immutable audit log of every signed binary).
 * Distinct from the "Code Signing & AV" status surface (/admin-services/
 * code-signing) — don't conflate the two.
 *
 * ENVELOPE: every /signing/* controller responds with the standard
 * `{ success, data }` envelope — verified against nurev-api
 * app/controllers/signing.js (every handler returns via
 * `utils.handleSuccess(res, 200, data)`). The legacy dashboard hook
 * (`useSigningQuery.js`) read these through an axiosClient that
 * auto-unwraps ONE level, then peeled the envelope again with
 * `unwrap = r => r?.data?.data ?? r?.data ?? r`.
 *
 * The admin `client` does NOT auto-unwrap, so we use `unwrap()` here —
 * it returns `r.data.data`, i.e. the inner payload directly.
 *
 * Paginated endpoints (requests, audit-log, my-requests) return the
 * mongoose-paginate shape: { docs, totalDocs, totalPages, page, limit }.
 */
import { client, unwrap } from './client';

// ─── Shared sub-shapes ──────────────────────────────────────────────────────

/** Populated creator ref ({ path: 'creatorId', select: 'email name publisherKey ...' }). */
export interface CreatorRef {
  _id: string;
  email?: string;
  name?: string;
  publisherKey?: string;
  signingTier?: SigningTier;
  identityVerificationStatus?: string;
}

/** Populated admin ref ({ path: 'approvedByAdmin' | 'rejectedByAdmin' | 'revokedBy', select: 'email name' }). */
export interface AdminRef {
  _id: string;
  email?: string;
  name?: string;
}

export interface BuildParams {
  productName?: string;
  companyName?: string;
  platform?: string;
  [key: string]: unknown;
}

export interface ValidationCheck {
  name: string;
  pass: boolean;
  note?: string;
}

export type SigningRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'auto_rejected'
  | 'building'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type SignedBinaryStatus = 'signed' | 'shipped' | 'completed' | 'revoked';

export type SigningTier = 'none' | 'manual-review' | 'automated';

// ─── Signing request (queue item / detail) ──────────────────────────────────

export interface SigningRequest {
  _id: string;
  creatorId?: CreatorRef | string | null;
  status: SigningRequestStatus | string;
  buildParams?: BuildParams;
  validationChecks?: ValidationCheck[];
  approvalTier?: string;
  approvedByAdmin?: AdminRef | string | null;
  rejectedByAdmin?: AdminRef | string | null;
  rejectionReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface SigningRequestListResponse {
  docs: SigningRequest[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface SigningRequestListParams {
  page?: number;
  limit?: number;
  status?: SigningRequestStatus | '';
  creatorId?: string;
  q?: string;
}

// ─── Signed-binary audit log ─────────────────────────────────────────────────

export interface AuditLogEntry {
  _id: string;
  creatorId?: CreatorRef | string | null;
  creatorEmailAtSigning?: string;
  status: SignedBinaryStatus | string;
  buildParams?: BuildParams;
  approvalTier?: string;
  binarySha256?: string;
  approvedByAdmin?: AdminRef | string | null;
  revokedBy?: AdminRef | string | null;
  revocationReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AuditLogListResponse {
  docs: AuditLogEntry[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface AuditLogListParams {
  page?: number;
  limit?: number;
  creatorId?: string;
  status?: SignedBinaryStatus | '';
  sha256?: string;
  q?: string;
}

// ─── Stats ────────────────────────────────────────────────────────────────

export interface StatusBucket {
  _id: string | null;
  count: number;
}

export interface SignedBinariesStat {
  total: number;
  last30d: number;
  revoked: number;
}

export interface SigningStats {
  requestsByStatus: StatusBucket[];
  signedBinaries: SignedBinariesStat;
  creatorsByTier: StatusBucket[];
}

// ─── Mutation responses ─────────────────────────────────────────────────────

export interface SetCreatorTierResponse {
  _id: string;
  signingTier: SigningTier;
  signingTierGrantedAt?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Drop empty/undefined/null query params before they hit the wire. */
function clean(params: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      out[k] = v;
    }
  });
  return out;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchSigningStats(): Promise<SigningStats> {
  return unwrap<SigningStats>(
    client.get<{ data: SigningStats }>('/signing/stats')
  );
}

export async function fetchSigningRequests(
  params: SigningRequestListParams
): Promise<SigningRequestListResponse> {
  return unwrap<SigningRequestListResponse>(
    client.get<{ data: SigningRequestListResponse }>('/signing/requests', {
      params: clean(params)
    })
  );
}

export async function fetchSigningRequest(id: string): Promise<SigningRequest> {
  return unwrap<SigningRequest>(
    client.get<{ data: SigningRequest }>(`/signing/requests/${id}`)
  );
}

export async function fetchAuditLog(
  params: AuditLogListParams
): Promise<AuditLogListResponse> {
  return unwrap<AuditLogListResponse>(
    client.get<{ data: AuditLogListResponse }>('/signing/audit-log', {
      params: clean(params)
    })
  );
}

export async function fetchAuditLogEntry(id: string): Promise<AuditLogEntry> {
  return unwrap<AuditLogEntry>(
    client.get<{ data: AuditLogEntry }>(`/signing/audit-log/${id}`)
  );
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export async function approveSigningRequest(
  id: string
): Promise<SigningRequest> {
  return unwrap<SigningRequest>(
    client.post<{ data: SigningRequest }>(`/signing/requests/${id}/approve`)
  );
}

export async function rejectSigningRequest(input: {
  id: string;
  reason: string;
}): Promise<SigningRequest> {
  return unwrap<SigningRequest>(
    client.post<{ data: SigningRequest }>(
      `/signing/requests/${input.id}/reject`,
      { reason: input.reason }
    )
  );
}

export async function revokeSignedBinary(input: {
  id: string;
  reason: string;
}): Promise<AuditLogEntry> {
  return unwrap<AuditLogEntry>(
    client.post<{ data: AuditLogEntry }>(
      `/signing/audit-log/${input.id}/revoke`,
      { reason: input.reason }
    )
  );
}

export async function setCreatorTier(input: {
  creatorId: string;
  tier: SigningTier;
}): Promise<SetCreatorTierResponse> {
  return unwrap<SetCreatorTierResponse>(
    client.patch<{ data: SetCreatorTierResponse }>(
      `/signing/creators/${input.creatorId}/tier`,
      { tier: input.tier }
    )
  );
}
